import { useEffect, useRef } from 'react';

export interface DataRefreshEventDetail {
  entidad: string;
  entidad_id?: string;
  accion: string;
  usuarioDestino?: string;
  // Solo poblados cuando entidad === 'notification' (ver notifyClients en server.ts) -- permiten
  // que el toast en real time de la campana muestre el contenido real del evento.
  titulo?: string;
  mensaje?: string;
  tipo?: string;
}

/**
 * Suscripción al broadcast SSE (06/08, explicit requirement: real time en toda la app, sin pisar
 * ediciones en curso). Antes disparaba `onRefresh` ante CUALQUIER cambio de cualquier entidad
 * en el tenant -- eso ya había obligado a un workaround manual en Accounting.tsx (evitar
 * rebuscar the record por filtro para no perder una edición) y a desactivar por completo el
 * broadcast del TRM ("Removed to prevent disruptive page reloads").
 *
 * - `entidades`: lista de entidades que le interesan a esta página (ej. ['record','quote']).
 *   Si el evento trae una entidad que no está en la lista, is ignored. Lista vacía = sin filtrar
 *   (compatibilidad con el comportamiento anterior, para páginas que aún no se han migrado).
 * - `opts.skipIds`: ids que la página tiene abiertos en edición ahora mismo -- si el evento es
 *   sobre uno de esos ids, is ignored (generaliza el workaround que Accounting.tsx ya hacía a mano).
 *
 * Usa un ref instead of re-suscribir el listener en cada render (evitaba que un `onRefresh`
 * inline recreado en cada render del padre desuscribiera/resuscribiera el listener global de
 * `window` sin necesidad -- mismo tipo de churn que se auditó y evitó en components/Modal.tsx).
 */
export const useDataRefresh = (
  entidades: string[],
  onRefresh: (detail?: DataRefreshEventDetail) => void,
  opts?: { skipIds?: string[] }
) => {
  const stateRef = useRef({ entidades, onRefresh, skipIds: opts?.skipIds });
  stateRef.current = { entidades, onRefresh, skipIds: opts?.skipIds };

  useEffect(() => {
    const handleRefresh = (event: Event) => {
      const { entidades: ents, onRefresh: cb, skipIds } = stateRef.current;
      const detail = (event as CustomEvent<DataRefreshEventDetail>).detail;
      if (ents.length > 0 && detail && !ents.includes(detail.entidad)) return;
      if (detail?.entidad_id && skipIds?.includes(detail.entidad_id)) return;
      cb(detail);
    };
    window.addEventListener('logistrade_refresh_data', handleRefresh);
    return () => window.removeEventListener('logistrade_refresh_data', handleRefresh);
  }, []);
};
